import { MembershipService } from '../MembershipService';

/**
 * Advanced Loop Detection System
 *
 * Prevents agent loops by tracking:
 * 1. Tool call sequences (detects A→B→A→B patterns)
 * 2. Tool call frequency (prevents spam)
 * 3. Cross-agent delegation chains (prevents A→B→A loops)
 *
 * Phase 2: Stability Fixes
 */

export interface ToolCall {
    name: string;
    args: string;
    timestamp: number;
    cost?: number;
}

export interface LoopDetectionResult {
    isLoop: boolean;
    reason?: string;
    pattern?: string;
}

export class LoopDetector {
    private toolCallHistory: ToolCall[] = [];
    private maxHistorySize = 20; // Track last 20 tool calls

    /**
     * Record a tool call
     */
    recordToolCall(name: string, args: Record<string, unknown>): void {
        const argsStr = JSON.stringify(args);

        // Estimate cost for expensive tools to prevent unmonitored credit burn (Bug C4)
        let cost = 0;
        if (name === 'generate_video' || name === 'indii_video_gen') {
            const duration = typeof args.duration === 'number' ? args.duration : 4;
            cost = duration * 0.20; // Approx $0.20 per second for 720p
        } else if (name === 'generate_image' || name === 'indii_image_gen') {
            cost = 0.04;
        } else if (name === 'generate_audio' || name === 'indii_audio_gen') {
            cost = 0.10;
        }

        this.toolCallHistory.push({
            name,
            args: argsStr,
            timestamp: Date.now(),
            cost
        });

        // Keep history bounded
        if (this.toolCallHistory.length > this.maxHistorySize) {
            this.toolCallHistory.shift();
        }
    }

    /**
     * Detect if current tool call would create a loop or exceed budget
     */
    async detectLoop(name: string, args: Record<string, unknown>): Promise<LoopDetectionResult> {
        const argsStr = JSON.stringify(args);

        // Checks 1–4 protect against BILLABLE spend loops. Free tools
        // (search, reads, document ops) legitimately repeat — an agent
        // re-reading the same file or querying several times in a row is
        // normal intent-chaining, not a loop; the agent's iteration budget
        // still bounds runaway free-tool rounds.
        const costedTool = [
            'generate_video',
            'indii_video_gen',
            'generate_image',
            'indii_image_gen',
            'generate_audio',
            'indii_audio_gen',
        ].includes(name);

        // Check 1: Exact same tool+args called twice in a row
        const lastCall = this.toolCallHistory[this.toolCallHistory.length - 1];
        if (costedTool && lastCall && lastCall.name === name && lastCall.args === argsStr) {
            return {
                isLoop: true,
                reason: 'Same tool with same arguments called consecutively',
                pattern: `${name}(${argsStr}) → ${name}(${argsStr})`
            };
        }

        // Check 2: Alternating pattern (A→B→A→B) — billable tools only
        if (costedTool && this.toolCallHistory.length >= 3) {
            const last3 = this.toolCallHistory.slice(-3);
            const [call1, call2, call3] = last3 as [typeof last3[number], typeof last3[number], typeof last3[number]];

            if (call1.name === call3.name && call1.args === call3.args &&
                call2.name === name && call2.args === argsStr) {
                return {
                    isLoop: true,
                    reason: 'Alternating pattern detected',
                    pattern: `${call1.name} → ${call2.name} → ${call3.name} → ${name}`
                };
            }
        }

        // Check 3: Tool frequency (same tool called too many times) — applies
        // to billable tools and the seating pair; free-tool frequency is left
        // to the iteration budget.
        const recentCalls = this.toolCallHistory.slice(-10); // Last 10 calls
        const sameToolCount = recentCalls.filter(call => call.name === name).length;
        // Cost-aware frequency ceiling. Image generation is ~$0.04/call and is
        // already budget-gated on every call (Check 7) plus the session cost cap
        // (Check 6), so a hard "5 in 10" was tripping legitimate multi-image
        // creative batches (record cover + artist likeness + refinements).
        // Expensive tools (video/audio) stay tight because one call can cost dollars.
        const MAX_SAME_TOOL_FREQUENCY =
            (name === 'unseat_agent' || name === 'seat_agent') ? 10 :
            (name === 'generate_image' || name === 'indii_image_gen') ? 12 :
            5;

        if ((costedTool || name === 'unseat_agent' || name === 'seat_agent') && sameToolCount >= MAX_SAME_TOOL_FREQUENCY) {
            return {
                isLoop: true,
                reason: `Tool '${name}' called ${sameToolCount} times in last ${recentCalls.length} calls`,
                pattern: `Excessive ${name} calls`
            };
        }

        // Check 4: Repeating sequence pattern (A→B→C→A→B→C). The signature
        // includes the arguments — six calls of the same tool with different
        // args (pagination, repeated lookups) is intent-chaining, not a loop;
        // only an exact (tool, args) sequence repetition is loop-shaped.
        if (costedTool && this.toolCallHistory.length >= 6) {
            const last6 = this.toolCallHistory.slice(-6);
            const signature = (call: ToolCall) => `${call.name}(${call.args})`;
            const sequence1 = last6.slice(0, 3).map(signature).join('→');
            const sequence2 = last6.slice(3, 6).map(signature).join('→');

            if (sequence1 === sequence2) {
                // Ignore sequences composed entirely of seating/unseating commands, as these often happen in large batches
                const isOnlySeatingCommands = last6.every(call => call.name === 'seat_agent' || call.name === 'unseat_agent');

                if (!isOnlySeatingCommands) {
                    return {
                        isLoop: true,
                        reason: 'Repeating sequence detected',
                        pattern: `${sequence1} → ${sequence2}`
                    };
                }
            }
        }

        // Check 5: Consecutive speak calls (anti-spam). The system prompt
        // explicitly directs agents to use `speak` for announcements, so two
        // speaks with other tools in between is normal intent-chaining — the
        // old rule (2 speaks in any 3 calls) killed legitimate runs with
        // "Task ended: Excessive consecutive speak calls". Only speaks with
        // NO other tool call between them are spam-shaped. The current call
        // is not recorded yet, so the previous recorded call is the test.
        if (name === 'speak') {
            const previousCall = this.toolCallHistory[this.toolCallHistory.length - 1];
            if (previousCall?.name === 'speak') {
                return {
                    isLoop: true,
                    reason: 'Excessive consecutive speak calls',
                    pattern: 'speak spam detected'
                };
            }
        }

        // Check 6: Cumulative Cost Safety Limit (Bug C4)
        let cumulativeCost = 0;
        for (const call of this.toolCallHistory) {
            cumulativeCost += call.cost || 0;
        }

        const MAX_SESSION_COST_LIMIT = 5.00; // $5 limit per agent session for anti-burn
        if (cumulativeCost >= MAX_SESSION_COST_LIMIT) {
            return {
                isLoop: true,
                reason: `Cumulative tool cost ($${cumulativeCost.toFixed(2)}) exceeded session safety limit ($${MAX_SESSION_COST_LIMIT.toFixed(2)})`,
                pattern: 'Cost limit exceeded'
            };
        }

        // Check 7: Real-time Budget Enforcement (Global Circuit Breaker)
        // This stops agents if the USER'S daily limit or SESSION hard limit is hit.
        
        // Estimate cost for this specific call to prevent unmonitored credit burn (Bug C4)
        let currentCost = 0;
        if (name === 'generate_video' || name === 'indii_video_gen') {
            const duration = typeof args.duration === 'number' ? args.duration : 4;
            currentCost = duration * 0.20; // Approx $0.20 per second for 720p
        } else if (name === 'generate_image' || name === 'indii_image_gen') {
            currentCost = 0.04;
        } else if (name === 'generate_audio' || name === 'indii_audio_gen') {
            currentCost = 0.10;
        }

        const canExecute = await MembershipService.checkBudget(currentCost);
        if (!canExecute.allowed) {
            return {
                isLoop: true,
                reason: canExecute.requiresApproval ? 'Action requires explicit user approval (cost ceiling reached)' : 'Budget limit exceeded or Emergency Kill Switch triggered. All agent operations suspended for safety.',
                pattern: 'Emergency Circuit Breaker Triggered'
            };
        }

        return { isLoop: false };
    }

    /**
     * Get call history for debugging
     */
    getHistory(): ToolCall[] {
        return [...this.toolCallHistory];
    }

    /**
     * Get recent pattern summary
     */
    getRecentPattern(): string {
        const recent = this.toolCallHistory.slice(-5);
        return recent.map(c => c.name).join(' → ');
    }

    /**
     * Clear history (use when starting new task)
     */
    clear(): void {
        this.toolCallHistory = [];
    }

    /**
     * Get statistics about tool usage
     */
    getStats(): { [toolName: string]: number } {
        const stats: { [toolName: string]: number } = {};
        this.toolCallHistory.forEach(call => {
            stats[call.name] = (stats[call.name] || 0) + 1;
        });
        return stats;
    }
}

/**
 * Cross-agent delegation loop detector
 * Tracks delegation chains to prevent A→B→A or A→B→C→A loops
 */
export class DelegationLoopDetector {
    private static delegationChains: Map<string, string[]> = new Map();

    /**
     * Record an agent delegation
     * @param swarmId Global swarm/session ID for this execution chain
     * @param agentId Agent being delegated to
     * @returns LoopDetectionResult
     */
    static recordDelegation(swarmId: string, agentId: string): LoopDetectionResult {
        const chain = this.delegationChains.get(swarmId) || [];

        // Check for loop: same agent appearing twice in chain
        const agentOccurrences = chain.filter(id => id === agentId).length;

        if (agentOccurrences >= 1) {
            return {
                isLoop: true,
                reason: `Agent '${agentId}' already in delegation chain`,
                pattern: [...chain, agentId].join(' → ')
            };
        }

        // Check for excessive delegation depth
        const MAX_DELEGATION_DEPTH = 4;
        if (chain.length >= MAX_DELEGATION_DEPTH) {
            return {
                isLoop: true,
                reason: `Delegation chain too deep (${chain.length} levels)`,
                pattern: [...chain, agentId].join(' → ')
            };
        }

        // Record this delegation
        chain.push(agentId);
        this.delegationChains.set(swarmId, chain);

        return { isLoop: false };
    }

    /**
     * Clean up completed delegation chain
     */
    static cleanup(swarmId: string): void {
        this.delegationChains.delete(swarmId);
    }

    /**
     * Get current delegation chain for debugging
     */
    static getChain(swarmId: string): string[] {
        return this.delegationChains.get(swarmId) || [];
    }
}
